/**
 * CustomerService – manages the user's customer selection list via the User Settings API.
 *
 * Employees and admins can maintain a list of customers they frequently order for.
 * This list is stored per-user in the User Settings API and used in the cart page
 * to select a customer instead of manually typing a customer number.
 *
 * API: GET/POST/PUT /api/user-settings
 * Schema: { list_of_customers_to_select: [{ customer_id, name, sort_order }] }
 */

import { ApiClient } from '../../shared/services/api-client';
import { eventBus } from '../../infrastructure/event-bus';
import { padCustomerNumber } from '../../shared/utils/pad-customer-number';
import type { CustomerEntry, UserSettingsRecord } from '../../shared/models/customer-entry.model';

// ---------------------------------------------------------------------------
// API Client
// ---------------------------------------------------------------------------

function getApiBaseUrl(): string {
  try {
    return (import.meta.env?.VITE_BASE_API_BASE_URL as string | undefined) ?? '';
  } catch {
    return '';
  }
}

const settingsApiClient = new ApiClient(getApiBaseUrl());

// ---------------------------------------------------------------------------
// CustomerService class
// ---------------------------------------------------------------------------

export class CustomerService {
  private customers: CustomerEntry[] = [];
  private recordId: string | null = null;
  private loading = false;
  private loaded = false;

  // -- Query ----------------------------------------------------------------

  /** Get the current list of customers, sorted by sort_order. */
  getCustomers(): CustomerEntry[] {
    return [...this.customers].sort(
      (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity),
    );
  }

  /** Whether the service is currently loading. */
  isLoading(): boolean {
    return this.loading;
  }

  /** Whether data has been loaded at least once. */
  isLoaded(): boolean {
    return this.loaded;
  }

  // -- Load -----------------------------------------------------------------

  /**
   * Load the customer list from the User Settings API.
   * Fetches the first record for the authenticated user.
   * Normalizes sort_order on load (assigns sequential values if missing).
   */
  async loadCustomers(): Promise<CustomerEntry[]> {
    this.loading = true;

    try {
      const records = await settingsApiClient.get<UserSettingsRecord[]>(
        '/api/user-settings?perPage=1',
      );

      if (records.length > 0) {
        this.recordId = records[0].id;
        const raw = records[0].list_of_customers_to_select ?? [];
        // Normalize: assign sort_order if missing
        this.customers = raw.map((c, index) => ({
          ...c,
          sort_order: c.sort_order ?? index,
        }));
      } else {
        this.recordId = null;
        this.customers = [];
      }

      this.loaded = true;
      this.notifyChange();
      return this.getCustomers();
    } finally {
      this.loading = false;
    }
  }

  // -- Mutations ------------------------------------------------------------

  /**
   * Add a new customer to the list.
   * Persists to the API immediately.
   * Assigns the next sort_order value automatically.
   */
  async addCustomer(entry: CustomerEntry): Promise<void> {
    // Normalize: pad customer_id with leading zeros to 10 digits
    const maxSortOrder = this.customers.reduce(
      (max, c) => Math.max(max, c.sort_order ?? 0),
      -1,
    );
    const normalizedEntry: CustomerEntry = {
      ...entry,
      customer_id: padCustomerNumber(entry.customer_id),
      sort_order: maxSortOrder + 1,
    };

    // Check for duplicate customer_id
    if (this.customers.some((c) => c.customer_id === normalizedEntry.customer_id)) {
      throw new Error('Kundennummer existiert bereits in der Liste.');
    }

    const updated = [...this.customers, normalizedEntry];
    await this.saveCustomers(updated);
    this.customers = updated;
    this.notifyChange();
  }

  /**
   * Update an existing customer entry.
   * Matches by the original customer_id. Preserves sort_order.
   */
  async updateCustomer(originalCustomerId: string, entry: CustomerEntry): Promise<void> {
    const index = this.customers.findIndex((c) => c.customer_id === originalCustomerId);
    if (index === -1) {
      throw new Error('Kunde nicht gefunden.');
    }

    // Normalize: pad customer_id with leading zeros to 10 digits
    const normalizedEntry: CustomerEntry = {
      ...entry,
      customer_id: padCustomerNumber(entry.customer_id),
      sort_order: entry.sort_order ?? this.customers[index].sort_order,
    };

    // Check for duplicate if customer_id changed
    if (
      normalizedEntry.customer_id !== originalCustomerId &&
      this.customers.some((c) => c.customer_id === normalizedEntry.customer_id)
    ) {
      throw new Error('Kundennummer existiert bereits in der Liste.');
    }

    const updated = [...this.customers];
    updated[index] = normalizedEntry;
    await this.saveCustomers(updated);
    this.customers = updated;
    this.notifyChange();
  }

  /**
   * Reorder the customer list by moving a customer from one position to another.
   * Recalculates sort_order for all entries and persists.
   *
   * @param fromCustomerId The customer_id of the entry being moved
   * @param toIndex The target index (in the currently sorted list)
   */
  async reorderCustomers(fromCustomerId: string, toIndex: number): Promise<void> {
    // Work with a sorted copy
    const sorted = this.getCustomers();
    const fromIndex = sorted.findIndex((c) => c.customer_id === fromCustomerId);
    if (fromIndex === -1) return;
    if (fromIndex === toIndex) return;

    // Remove from old position and insert at new position
    const [moved] = sorted.splice(fromIndex, 1);
    sorted.splice(toIndex, 0, moved);

    // Reassign sort_order sequentially
    const updated = sorted.map((c, i) => ({ ...c, sort_order: i }));
    await this.saveCustomers(updated);
    this.customers = updated;
    this.notifyChange();
  }

  /**
   * Remove a customer from the list by customer_id.
   */
  async removeCustomer(customerId: string): Promise<void> {
    const updated = this.customers.filter((c) => c.customer_id !== customerId);
    await this.saveCustomers(updated);
    this.customers = updated;
    this.notifyChange();
  }

  // -- Persistence ----------------------------------------------------------

  /**
   * Save the full customer list to the API.
   * Creates a new record if none exists, otherwise updates the existing one.
   */
  private async saveCustomers(customers: CustomerEntry[]): Promise<void> {
    const body = { list_of_customers_to_select: customers };

    if (this.recordId) {
      // Update existing record
      const response = await settingsApiClient.put<UserSettingsRecord>(
        `/api/user-settings/${encodeURIComponent(this.recordId)}`,
        body,
      );
      this.recordId = response.id;
    } else {
      // Create new record
      const response = await settingsApiClient.post<UserSettingsRecord>(
        '/api/user-settings',
        body,
      );
      this.recordId = response.id;
    }
  }

  // -- Reset ----------------------------------------------------------------

  /** Reset local state (e.g. on logout). */
  reset(): void {
    this.customers = [];
    this.recordId = null;
    this.loaded = false;
    this.notifyChange();
  }

  // -- Events ---------------------------------------------------------------

  private notifyChange(): void {
    eventBus.emit('customers-changed', { customers: this.customers });
  }

  /** Subscribe to customer list changes. Returns an unsubscribe function. */
  onCustomersChanged(callback: (customers: CustomerEntry[]) => void): () => void {
    return eventBus.on<{ customers: CustomerEntry[] }>('customers-changed', (detail) => {
      callback(detail.customers);
    });
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

/** Global CustomerService instance shared across the application. */
export const customerService = new CustomerService();
